import { useCallback } from 'react';
import { useWizard } from 'react-use-wizard';
import { useSelector } from 'react-redux';
import { AlertTriangle, CheckCircle } from 'lucide-react';

import { StepLayout } from '@/features/guided-financial-planning/components/step-layout';
import { getStepIndex } from '@/features/guided-financial-planning/config';
import { useGuidedPlanningFlatStepConfigs } from '@/features/guided-financial-planning/context';
import { useIsEdjPilotAdvisor } from '@/shared/hooks/use-edj-pilot-advisor';
import { useCareHasStarted } from '@/shared/hooks/use-care-has-started';
import { selectFeatureFlags } from '@client/model/selectors';
import {
  CoverageAssessmentResults,
  useCoverageAssessmentVariant,
} from './coverage-assessment-results';

export function AssessmentProjectionStep() {
  const { nextStep, previousStep, goToStep } = useWizard();
  const { hasGap } = useCoverageAssessmentVariant();
  const isEdjPilot = useIsEdjPilotAdvisor();
  const careHasStarted = useCareHasStarted();
  const featureFlags = useSelector(selectFeatureFlags);
  const isCcrcTypeA = !!featureFlags.ccrcTypeAProducts;
  const skipPolicySteps = isEdjPilot || careHasStarted;
  const flatStepConfigs = useGuidedPlanningFlatStepConfigs();

  const handleNext = useCallback(() => {
    if (skipPolicySteps) {
      goToStep(getStepIndex('plan-comparison', flatStepConfigs));
    } else {
      nextStep();
    }
  }, [skipPolicySteps, goToStep, nextStep, flatStepConfigs]);

  const nextButtonLabel = isCcrcTypeA
    ? 'Show Me My Type A Plan'
    : skipPolicySteps
      ? 'Continue to Plan Comparison'
      : hasGap
        ? 'Yes, Find Insurance to Close the Gap'
        : 'Show Me Efficient Protection Options';

  const icon = hasGap ? (
    <div className="bg-primary/10 flex size-14 items-center justify-center rounded-full p-2">
      <AlertTriangle className="text-primary size-8" />
    </div>
  ) : (
    <div className="bg-primary/10 flex size-14 items-center justify-center rounded-full p-2">
      <CheckCircle className="text-primary size-8" />
    </div>
  );

  return (
    <StepLayout
      title="Your Coverage Assessment"
      subtitle=""
      icon={icon}
      nextButtonLabel={nextButtonLabel}
      onNext={handleNext}
      onBack={previousStep}
    >
      <CoverageAssessmentResults />
    </StepLayout>
  );
}
